import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NgButton } from '@/ui';
import { CollaborationState } from '@/signalr';
import {
  confirmCollaboration as confirmCollaborationCommand,
  openConfirmOrRejectIncidentShareDialog,
  rejectCollaboration as rejectCollaborationCommand,
} from '../actions';
import { IncidentBanner } from './IncidentBanner';

export type IncidentDetailCollaborationStateBannerProps = {
  incidentId: string;
  collaborationState: CollaborationState;
};

const FORCE_CONFIRMATION_DELAY_MS = 60_000;

export function IncidentDetailCollaborationStateBanner({
  incidentId,
  collaborationState,
}: IncidentDetailCollaborationStateBannerProps) {
  const { t } = useTranslation();
  const [isInProgress, setIsInProgress] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const isDialogOpenRef = useRef(false);
  const confirmationTimerStoppedRef = useRef(false);

  const confirmCollaboration = async () => {
    confirmationTimerStoppedRef.current = true;
    setIsInProgress(true);
    await confirmCollaborationCommand(incidentId);
    setIsInProgress(false);

    setShowConfirmation(true);
    setTimeout(() => setShowConfirmation(false), 5000);
  };

  const rejectCollaboration = async () => {
    confirmationTimerStoppedRef.current = true;
    setIsInProgress(true);
    await rejectCollaborationCommand(incidentId);
    setIsInProgress(false);
  };

  useEffect(() => {
    if (collaborationState !== CollaborationState.Invited) {
      return;
    }

    confirmationTimerStoppedRef.current = false;
    const timer = setTimeout(() => {
      if (!confirmationTimerStoppedRef.current && !isDialogOpenRef.current) {
        isDialogOpenRef.current = true;
        openConfirmOrRejectIncidentShareDialog(incidentId, async (result) => {
          if (result) {
            await confirmCollaboration();
          } else {
            await rejectCollaboration();
          }
          isDialogOpenRef.current = false;
        });
      }
    }, FORCE_CONFIRMATION_DELAY_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collaborationState, incidentId]);

  if (showConfirmation) {
    return <IncidentBanner type="success">{t('Incident_Collaboration_IncidentWasConfirmedBannerText')}</IncidentBanner>;
  }

  if (collaborationState === CollaborationState.Invited) {
    return (
      <IncidentBanner type="info" narrow cssClass="flash-incident-confirmation-banner">
        <span className="me-2">{t('Incident_Collaboration_AwaitingConfirmation')}</span>
        <div className="d-flex gap-1">
          <NgButton
            type="dark"
            text={t('Incident_Collaboration_ConfirmButton')}
            size="small"
            onClick={confirmCollaboration}
            disabled={isInProgress}
            showProgress
            enableMultipleClickPrevention
          />
          <NgButton
            type="dark"
            text={t('Incident_Collaboration_RejectButton')}
            size="small"
            onClick={rejectCollaboration}
            disabled={isInProgress}
            showProgress
            enableMultipleClickPrevention
          />
        </div>
      </IncidentBanner>
    );
  }

  if (collaborationState === CollaborationState.Declined) {
    return <IncidentBanner type="dark">{t('Incident_Collaboration_IncidentWasRejectedBannerText')}</IncidentBanner>;
  }

  return null;
}
